<template>
  <div class="count-progress" :style="containerStyle">
    <!-- Title row -->
    <div class="d-flex align-center mb-1">
      <div class="d-flex align-center">
        <v-icon v-if="icon" small class="mr-1">{{ icon }}</v-icon>
        <span class="text-caption font-weight-medium">{{ title || '' }}</span>
      </div>
      <v-spacer></v-spacer>
      <span v-if="unlimited" class="text-caption text--secondary">
        {{ unlimitedLabel }}
      </span>
    </div>
    
    <!-- Combined Progress Bar -->
    <div class="progress-container" :style="{height: `${computedHeight}px`}">
      <!-- Global limit background -->
      <div 
        v-if="showGlobalLimit && globalLimit > 0" 
        class="global-limit-bar"
        :style="{
          width: '100%', 
          background: 'linear-gradient(to right, rgba(0,0,0,0.02), rgba(0,0,0,0.08))'
        }"
      ></div>
      
      <!-- Facility limit indicator -->
      <div 
        v-if="!unlimited && limit > 0" 
        class="limit-indicator"
        :style="{
          left: `${limitToGlobalPercent}%`,
          height: `${computedHeight + 6}px`,
          top: '-3px',
          width: '3px',
          backgroundColor: isOverLimit ? 'rgba(244,67,54,0.7)' : 'rgba(0,0,0,0.4)',
          position: 'absolute',
          zIndex: 3,
          boxShadow: '0 0 3px rgba(0,0,0,0.2)',
          borderRadius: '2px'
        }"
      ></div>
      
      <!-- Usage bar -->
      <v-progress-linear
        :value="isOverLimit ? 100 : percent"
        :color="barColor"
        :height="computedHeight"
        :rounded="rounded"
        :background-opacity="0.1"
        :striped="isOverLimit"
        :indeterminate="false"
        class="usage-bar"
        style="position: absolute; top: 0; left: 0; width: 100%;"
      >
        <template v-if="isOverLimit" v-slot:default>
          <span class="white--text caption font-weight-medium">!</span>
        </template>
      </v-progress-linear>
    </div>
    
    <!-- Values display -->
    <div class="d-flex justify-space-between mt-1">
      <span class="text-caption" :class="{'error--text': isOverLimit}">{{ formatValue(used) }}</span>
      <span class="text-caption limit-value" :class="{'error--text': isOverLimit}">
        {{ formatValue(limit) }}
        <v-tooltip v-if="isOverLimit" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon x-small color="error" class="ml-1" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
          </template>
          <span>Exceeds limit by {{ formatValue(used - safeLimit) }}</span>
        </v-tooltip>
      </span>
      <span v-if="showGlobalLimit && globalLimit > 0" class="text-caption text--secondary global-value">
        {{ formatValue(globalLimit) }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CountProgress',
  props: {
    count: { type: [Number, String], default: 0 },
    limit: { type: [Number, String], default: 0 },
    globalLimit: { type: [Number, String], default: 0 },
    minWidth: { type: [Number, String], default: 240 },
    height: { type: [Number, String], default: 8 },
    rounded: { type: Boolean, default: true },
    successColor: { type: String, default: 'primary' },
    warningColor: { type: String, default: 'warning' },
    errorColor: { type: String, default: 'error' },
    warnThreshold: { type: Number, default: 70 },
    errorThreshold: { type: Number, default: 90 },
    showCountLabel: { type: Boolean, default: true },
    showGlobalLimit: { type: Boolean, default: true },
    icon: { type: String, default: '' },
    title: { type: String, default: '' },
    unlimited: { type: Boolean, default: false },
    unlimitedLabel: { type: String, default: 'Unlimited' },
    // Accept either boolean true or the string 'fileSize'. When enabled, formats values as MB/GB/TB.
    type: { type: [Boolean, String], default: false },
    // Visual size of the widget. Default 'small' keeps current sizes.
    // When set to 'medium' or 'large', height and font size increase.
    size: { type: String, default: 'small', validator: v => ['small', 'medium', 'large'].includes(v) },
  },
  computed: {
    used() {
      return Number(this.count) || 0;
    },
    safeLimit() {
      if (this.unlimited) return Number.MAX_SAFE_INTEGER;
      const lim = Number(this.limit) || 0;
      return lim > 0 ? lim : 1; // avoid division by zero
    },
    safeGlobalLimit() {
      const lim = Number(this.globalLimit) || 0;
      return lim > 0 ? lim : 1;
    },
    percent() {
      if (this.unlimited) return 0;
      if (!this.safeLimit) return 0;
      return Math.round((this.used / this.safeLimit) * 100);
    },
    globalPercent() {
      if (!this.safeGlobalLimit) return 0;
      return Math.min(100, Math.round((this.used / this.safeGlobalLimit) * 100));
    },
    limitToGlobalPercent() {
      if (this.unlimited || !this.safeGlobalLimit || this.safeGlobalLimit <= 0) return 100;
      return Math.min(100, Math.round((this.safeLimit / this.safeGlobalLimit) * 100));
    },
    isOverLimit() {
      return !this.unlimited && this.used > this.safeLimit;
    },
    barColor() {
      if (this.isOverLimit) return this.errorColor;
      if (this.percent >= this.errorThreshold) return this.errorColor;
      if (this.percent >= this.warnThreshold) return this.warningColor;
      return this.successColor;
    },
    sizeScale() {
      switch (this.size) {
        case 'large': return 1.5;
        case 'medium': return 1.2;
        default: return 1.0;
      }
    },
    computedHeight() {
      const base = Number(this.height) || 8;
      return Math.round(base * this.sizeScale);
    },
    containerStyle() {
      return { 
        minWidth: typeof this.minWidth === 'number' ? `${this.minWidth}px` : String(this.minWidth),
        fontSize: `${this.sizeScale * 0.9}em`
      };
    },
  },
  methods: {
    formatValue(value) {
      // Display infinity symbol for -1 limit values
      if (value === -1) {
        return '∞';
      }
      if (this.type === true || this.type === 'fileSize') {
        return this.formatSizeMB(value);
      }
      return value.toLocaleString();
    },
    formatSizeMB(mb) {
      const v = Number(mb) || 0;
      const abs = Math.abs(v);
      const GB = 1024;
      const TB = 1024 * 1024;
      const fmt = (num) => {
        const s = num.toFixed(2);
        return s.replace(/\.00$/, '');
      };
      if (abs >= TB) return `${fmt(v / TB)} TB`;
      if (abs >= GB) return `${fmt(v / GB)} GB`;
      return `${Math.round(v)} MB`;
    },
  },
};
</script>

<style scoped>
.count-progress {
  width: 100%;
  padding: 4px 0;
}

.progress-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 4px;
}

.v-progress-linear {
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.text-caption {
  line-height: 1.2;
  font-size: 0.75rem;
}

.global-limit-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 1;
  transition: background 0.3s ease;
}

.limit-indicator {
  transition: all 0.3s ease;
}

.usage-bar {
  transition: all 0.3s ease;
}

.limit-value, .global-value {
  transition: color 0.3s ease;
}

.v-progress-linear--striped .v-progress-linear__determinate {
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 40px 40px;
  animation: progress-linear-stripes 2s linear infinite;
}

@keyframes progress-linear-stripes {
  0% {
    background-position: 40px 0;
  }
  100% {
    background-position: 0 0;
  }
}
</style>
